import z from "zod";

const inspectifyErrorTypeValues = [
  "CREATE_ORDER_RESPONSE_INVALID",
  "GET_INSPECTIONS_RESPONSE_INVALID",
  "GET_AVAILABLE_BOOKING_TIMES_RESPONSE_INVALID",
  "GET_INSPECTION_PRICE_RESPONSE_INVALID",
  "GET_ORDERS_RESPONSE_INVALID",
  "GET_REPORTS_RESPONSE_INVALID",
  "INSPECTIFY_FEATURE_DISABLED",
  "INSPECTIFY_ORDER_CANCEL_FAILED",
  "INSPECTIFY_ORDER_LOOKUP_INCOMPLETE",
  "INSPECTIFY_WORK_ORDER_UPDATE_FAILED",
  "INSPECTIFY_WORK_ORDER_ACCEPT_FAILED",
] as const;

const inspectifyErrorTypeSchema = z.enum(inspectifyErrorTypeValues);
const InspectifyErrorType = inspectifyErrorTypeSchema.enum;
type InspectifyErrorType = z.infer<typeof inspectifyErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type InspectifyErrors =
  | {
      errorType:
        | typeof InspectifyErrorType.CREATE_ORDER_RESPONSE_INVALID
        | typeof InspectifyErrorType.GET_INSPECTIONS_RESPONSE_INVALID
        | typeof InspectifyErrorType.GET_AVAILABLE_BOOKING_TIMES_RESPONSE_INVALID
        | typeof InspectifyErrorType.GET_INSPECTION_PRICE_RESPONSE_INVALID
        | typeof InspectifyErrorType.GET_ORDERS_RESPONSE_INVALID
        | typeof InspectifyErrorType.GET_REPORTS_RESPONSE_INVALID;
      data: {
        // Zod issue paths only — the received body carries homeowner PII and must not reach Sentry.
        issuePaths: string[];
      };
    }
  | {
      errorType: typeof InspectifyErrorType.INSPECTIFY_FEATURE_DISABLED;
      data: { flag: string };
    }
  | {
      errorType: typeof InspectifyErrorType.INSPECTIFY_ORDER_CANCEL_FAILED;
      data: { remiProjectId: number; orderId: string; cancellationReason: string };
    }
  | {
      errorType: typeof InspectifyErrorType.INSPECTIFY_ORDER_LOOKUP_INCOMPLETE;
      data: { remiProjectId: number; scannedPages: number };
    }
  | {
      errorType:
        | typeof InspectifyErrorType.INSPECTIFY_WORK_ORDER_UPDATE_FAILED
        | typeof InspectifyErrorType.INSPECTIFY_WORK_ORDER_ACCEPT_FAILED;
      data: { remiProjectId: number; orderId: string; reason: string };
    };

export const inspectifyErrorDetails: Record<InspectifyErrorType, ErrorDetails> = {
  [InspectifyErrorType.CREATE_ORDER_RESPONSE_INVALID]: {
    sentryDescription: "Inspectify create-order response failed schema validation",
  },
  [InspectifyErrorType.GET_INSPECTIONS_RESPONSE_INVALID]: {
    sentryDescription: "Inspectify get-inspections response failed schema validation",
  },
  [InspectifyErrorType.GET_AVAILABLE_BOOKING_TIMES_RESPONSE_INVALID]: {
    sentryDescription: "Inspectify get-available-booking-times response failed schema validation",
  },
  [InspectifyErrorType.GET_INSPECTION_PRICE_RESPONSE_INVALID]: {
    sentryDescription: "Inspectify get-inspection-price response failed schema validation",
  },
  [InspectifyErrorType.GET_ORDERS_RESPONSE_INVALID]: {
    sentryDescription: "Inspectify get-orders response failed schema validation",
  },
  [InspectifyErrorType.GET_REPORTS_RESPONSE_INVALID]: {
    sentryDescription: "Inspectify get-reports response failed schema validation",
  },
  [InspectifyErrorType.INSPECTIFY_FEATURE_DISABLED]: {
    sentryDescription: "Inspectify integration feature flag is disabled",
  },
  [InspectifyErrorType.INSPECTIFY_ORDER_CANCEL_FAILED]: {
    sentryDescription: "Inspectify order cancel failed; the order may still be live",
  },
  [InspectifyErrorType.INSPECTIFY_ORDER_LOOKUP_INCOMPLETE]: {
    sentryDescription: "Inspectify order lookup exhausted its page budget before the order list ended",
  },
  [InspectifyErrorType.INSPECTIFY_WORK_ORDER_UPDATE_FAILED]: {
    sentryDescription: "Inspectify work order update returned a non-throwing failure; CREATE_WORK_ORDER may be stuck",
  },
  [InspectifyErrorType.INSPECTIFY_WORK_ORDER_ACCEPT_FAILED]: {
    sentryDescription: "Inspectify work order accept returned a non-throwing failure; INSTALL_SCHEDULED gate at risk",
  },
};
